import moment from 'moment'

export enum Period {
  'Daily' = 1,
  'Monthly' = 2,
  'Trimester' = 3,
  'Yearly' = 4,
  'Fixed' = 5,
  'Custom' = 6
}

export enum ErrorReason {
  datesNotValid = 'datesNotValid', // startDate must be before endDate
  periodNotValid = 'periodNotValid', // period not valid
  missingCustomNumRows = 'missingCustomNumRows', // 'Must provide customNumRows'
  notDivisible = 'notDivisible' // 'dates not divisible by period. Fix dates or remove Strict Mode.'
}

const getPeriodConfig = (period: Period): moment.unitOfTime.Diff | null => {
  let periodConfig: moment.unitOfTime.Diff | null = null

  switch (period) {
    case Period.Daily:
      periodConfig = 'days'
      break
    case Period.Monthly:
    case Period.Trimester:
      periodConfig = 'months'
      break
    case Period.Yearly:
      periodConfig = 'years'
      break
  }

  return periodConfig
}

const getPeriodMonths = (period: Period): number => {
  let periodMonths: number = 0

  switch (period) {
    case Period.Daily:
      periodMonths = 0.3
      break
    case Period.Monthly:
      periodMonths = 1
      break
    case Period.Trimester:
      periodMonths = 3
      break
    case Period.Yearly:
      periodMonths = 12
      break
  }

  return periodMonths
}

export interface GridRows {
  startDate: string
  endDate: string
  quantity: string | number | null
}

type GridValuesGeneratorParams = {
  startDate: Date
  endDate: Date
  period: Period
  customNumRows?: number
  strictMode?: boolean
  fixedQuantity?: number | string | null
}

export const gridValuesGenerator = ({
  startDate,
  endDate,
  period,
  customNumRows,
  strictMode,
  fixedQuantity = null
}: GridValuesGeneratorParams): { value: GridRows[] | null; reason: string | null } => {
  let numberRows = 0
  let periodConfig: moment.unitOfTime.Diff = 'days'
  let array: GridRows[] = []

  // if (!moment(startDate).isValid()) {
  //   throw new Error('startDate must be a date')
  // }
  // if (!moment(endDate).isValid()) {
  //   throw new Error('endDate must be a date')
  // }
  // if (typeof period !== 'number') {
  //   throw new Error('period must be a Period type')
  // }
  if (!moment(startDate).isSameOrBefore(moment(endDate), 'days')) {
    return { value: null, reason: ErrorReason.datesNotValid }
  }

  if (!Object.values(Period).includes(period)) {
    return { value: null, reason: ErrorReason.periodNotValid }
  }

  if (period === Period.Custom && !customNumRows) {
    return { value: null, reason: ErrorReason.missingCustomNumRows }
  }

  // Unless is daily, we will use the first of the month and the last day of the month
  const realStartDate =
    period === Period.Monthly || period === Period.Trimester || period === Period.Yearly
      ? moment(startDate).startOf('month')
      : startDate
  const realEndDate =
    period === Period.Monthly || period === Period.Trimester || period === Period.Yearly
      ? moment(endDate).endOf('month')
      : endDate

  periodConfig = getPeriodConfig(period) ?? 'days'

  if (
    strictMode &&
    (period === Period.Monthly || period === Period.Trimester || period === Period.Yearly)
  ) {
    let isValid = true
    if (period !== Period.Monthly) {
      const periodMonths = getPeriodMonths(period)
      isValid = (moment(realEndDate).diff(realStartDate, 'months') + 1) % periodMonths === 0
    }

    if (!isValid) {
      return { value: null, reason: ErrorReason.notDivisible }
    }
  }

  numberRows =
    period === Period.Custom && customNumRows
      ? customNumRows
      : period === Period.Trimester
        ? Math.ceil((moment(realEndDate).diff(realStartDate, periodConfig) + 1) / 3)
        : period === Period.Fixed
          ? 1
          : moment(realEndDate).diff(realStartDate, periodConfig) + 1

  const endDateCorrection = i => {
    switch (period) {
      case Period.Trimester:
        return i * 3 + 2
        break
      case Period.Yearly:
        return i * 12 + 11
      default:
        return i
    }
  }

  if (period === Period.Fixed) {
    const result = [
      {
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        quantity: ''
      }
    ]
    return { value: result, reason: null }
  }

  for (let i = 0; i < numberRows; i++) {
    const start =
      period === Period.Custom
        ? ''
        : moment(startDate)
            .add(period === Period.Trimester ? i * 3 : i, periodConfig)
            .startOf(
              period === Period.Monthly || period === Period.Trimester || period === Period.Yearly
                ? 'month'
                : 'day'
            )
            .format('YYYY-MM-DD')
    const end =
      period === Period.Custom
        ? ''
        : moment(startDate)
            .add(endDateCorrection(i), periodConfig !== 'days' ? 'months' : 'days')
            .endOf(
              period === Period.Monthly || period === Period.Trimester || period === Period.Yearly
                ? 'month'
                : 'day'
            )
            .format('YYYY-MM-DD')
    const row = {
      startDate: start,
      endDate: end,
      quantity: fixedQuantity
    }
    array.push(row)
  }

  // if (period === Period.Yearly) {
  //   console.log(array)
  // }

  return { value: array, reason: null }
}
